import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Custom RouteReuseStrategy that prevents reusing the QuizStepComponent
 * when navigating between different quiz steps.
 *
 * This ensures that animations work correctly as components are properly
 * destroyed and recreated between navigations.
 */
export class QuizRouteReuseStrategy implements RouteReuseStrategy {
  /**
   * Determines if the route should be detached for later reuse.
   * Always returns false for quiz routes to prevent reuse.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  /**
   * Stores the detached route.
   * Not used since we're not detaching routes.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    // Do nothing since we're not reusing routes
  }

  /**
   * Determines if the route should be reattached.
   * Always returns false since we're not storing detached routes.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  /**
   * Retrieves the stored route.
   * Always returns null since we're not storing routes.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return null;
  }

  /**
   * Determines if the same instance of the component should be reused.
   * Returns false for quiz routes to ensure a new instance is created each time.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // For quiz routes, never reuse the component
    if (future.routeConfig?.path === 'quiz/:index' && curr.routeConfig?.path === 'quiz/:index') {
      return false;
    }

    // For other routes, use the default strategy (reuse if it's the same route)
    return future.routeConfig === curr.routeConfig;
  }
}
